import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const features = [
  { text: 'Ekonomik fiyatlar', icon: 'ri-money-dollar-circle-line' },
  { text: 'Hızlı taksi', icon: 'ri-speed-line' },
  { text: 'Şehirler arası yolculuk', icon: 'ri-map-pin-range-line' },
  { text: 'Kredi Kartı ile Ödeme', icon: 'ri-bank-card-line' },
];

export default function About() {
  const { ref: leftRef, inView: leftInView } = useInView(0.1);
  const { ref: rightRef, inView: rightInView } = useInView(0.1);

  return (
    <section id="hakkimizda" className="relative py-28 bg-zinc-950 overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-24 bg-white" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 0, 0 100%)' }} />
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">

          {/* Sol - Görseller */}
          <div
            ref={leftRef}
            className="relative h-[600px]"
            style={{
              opacity: leftInView ? 1 : 0,
              transform: leftInView ? 'translateX(0)' : 'translateX(-70px)',
              transition: 'all 1s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            <div className="absolute top-0 left-0 w-[320px] h-[320px] rounded-2xl overflow-hidden shadow-2xl z-10 border-[6px] border-yellow-400 group hover:scale-105 transition-transform duration-500">
              <img
                src="/2.jpeg"
                alt="Başharık Taksi Malatya - Profesyonel Taksi Aracı ve Şehir İçi Ulaşım Hizmeti"
                className="w-full h-full object-cover object-center"
                style={{ objectPosition: 'center top', transform: 'scale(1.1)' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>

            <div className="absolute bottom-0 right-0 w-[360px] h-[440px] rounded-2xl overflow-hidden shadow-2xl border-[6px] border-yellow-400 group hover:scale-105 transition-transform duration-500">
              <img
                src="/5.jpeg"
                alt="Başharık Taksi Malatya - Deneyimli ve Profesyonel Taksi Şoförü"
                className="w-full h-full object-cover object-center"
                style={{ objectPosition: 'center top', transform: 'scale(1.1)' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>

            <div className="absolute top-10 right-10 w-20 h-1 bg-yellow-400" />
            <div className="absolute bottom-10 left-10 w-20 h-1 bg-yellow-400" />
          </div>

          {/* Sağ - İçerik */}
          <div
            ref={rightRef}
            style={{
              opacity: rightInView ? 1 : 0,
              transform: rightInView ? 'translateX(0)' : 'translateX(70px)',
              transition: 'all 1s cubic-bezier(0.16,1,0.3,1) 0.2s',
            }}
          >
            <div className="inline-flex items-center space-x-2 bg-yellow-400 px-6 py-2.5 rounded-full mb-7 shadow-lg">
              <i className="ri-history-line text-black text-base" />
              <span className="text-black text-xs font-black uppercase tracking-widest whitespace-nowrap">1998'den Beri</span>
            </div>

            <h2 className="text-6xl font-black text-white mb-7 leading-tight">
              Başharık Taksi<br />
              <span className="text-yellow-400">Güvenli Yolculuğun Adresi</span>
            </h2>

            <div
              className="h-1.5 bg-yellow-400 mb-8"
              style={{
                width: rightInView ? '96px' : '0px',
                transition: 'width 1s ease 0.5s',
              }}
            />

            <p className="text-gray-400 leading-relaxed mb-10 text-lg">
              Başharık Taksi olarak şehir içi ve şehirler arası tüm ulaşım ihtiyaçlarınıza cevap veriyoruz. Deneyimli şoförümüz, bakımlı aracımız ile güvenli ve konforlu yolculuk garantisi sunuyoruz. Kredi kartı ile ödeme yapabilirsiniz.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-10">
              {features.map((f, i) => (
                <div
                  key={f.text}
                  className="flex items-center space-x-3 group cursor-pointer"
                  style={{
                    opacity: rightInView ? 1 : 0,
                    transform: rightInView ? 'translateX(0)' : 'translateX(30px)',
                    transition: `all 0.6s ease ${0.4 + i * 0.08}s`,
                  }}
                >
                  <div className="w-7 h-7 flex items-center justify-center bg-yellow-400 rounded-lg flex-shrink-0 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                    <i className={`${f.icon} text-sm text-black`} />
                  </div>
                  <span className="text-gray-300 text-base group-hover:text-yellow-400 transition-colors duration-300 whitespace-nowrap font-semibold">{f.text}</span>
                </div>
              ))}
            </div>

            <a
              href="tel:05308920944"
              className="inline-flex items-center space-x-3 bg-yellow-400 hover:bg-white text-black px-10 py-5 rounded-full font-black transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 cursor-pointer whitespace-nowrap text-lg group"
            >
              <i className="ri-phone-fill text-2xl group-hover:rotate-12 transition-transform duration-300" />
              <span>Hemen Arayın</span>
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-24 bg-white" style={{ clipPath: 'polygon(0 100%, 100% 100%, 100% 0, 0 100%)' }} />

      <style>{`
        @keyframes floatBadge {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -54%) scale(1.04); }
        }
      `}</style>
    </section>
  );
}
